import React, { useState, useRef, useEffect } from 'react'
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Image,
  InputGroup,
  Spinner
} from 'react-bootstrap'
import {
  FaSearch,
  FaPhone,
  FaVideo,
  FaUsers,
  FaUserFriends,
  FaEllipsisH,
  FaSmile,
  FaPaperclip,
  FaPaperPlane,
  FaDownload,
  FaEllipsisV,
  FaUser,
  FaThumbsUp,
  FaAddressBook,
  FaCog,
  FaGlobe,
  FaMoon,
  FaBars,
  FaTimes,
  FaChevronDown,
  FaChevronUp
} from 'react-icons/fa'
import axios from 'axios'
import './ChatPage.css'
import GlobalMiniSidebar from './GlobalMiniSidebar'
import messageService from '../services/messageService'

const ChatPage = () => {
  const [message, setMessage] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [activeContact, setActiveContact] = useState({ name: '', email: '' })
  const [showSidebar, setShowSidebar] = useState(false)
  const [showRecentEmails, setShowRecentEmails] = useState(true)
  const [userEmails, setUserEmails] = useState([])
  const [recentMessages, setRecentMessages] = useState([])
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [searchTimeout, setSearchTimeout] = useState(null)
  const [isSearching, setIsSearching] = useState(false)

  const [showSearch, setShowSearch] = useState(false)
  const [searchQueryChat, setSearchQueryChat] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [isSearchingChat, setIsSearchingChat] = useState(false)

  const messagesEndRef = useRef(null)
  const messageContainerRef = useRef(null)
  const sidebarRef = useRef(null)
  const searchInputRef = useRef(null)

  // Fetch user email list on mount
  // useEffect(() => {
  //     setLoadingUsers(true);
  //     messageService.getAllMessagesGroupedByEmail()
  //         .then(res => {
  //             if (!res.data.error) {
  //                 setUserEmails(res.data);
  //                 if (res.data.length > 0) {
  //                     handleUserClick(res.data[0]);
  //                 }
  //             }
  //         })
  //         .catch(err => console.error('Error fetching user emails:', err))
  //         .finally(() => setLoadingUsers(false));
  // }, []);

  // Fetch user email list on mount
  useEffect(() => {
    fetchUserEmails()
  }, [])

  // Fetch user emails with optional search
  const fetchUserEmails = async (search = '') => {
    setLoadingUsers(true)
    try {
      const res = await messageService.getAllMessagesGroupedByEmail({
        search
      })
      if (!res.data.error) {
        setUserEmails(res.data)
        if (res.data.length > 0 && !activeContact.email) {
          handleUserClick(res.data[0])
        }
      }
    } catch (err) {
      console.error('Error fetching user emails:', err)
    } finally {
      setLoadingUsers(false)
      setIsSearching(false)
    }
  }

  // Handle search input changes with debounce
  // const handleSearchChange = (e) => {
  //     const query = e.target.value;
  //     setSearchQuery(query);
  //     setIsSearching(true);

  //     // Clear any existing timeout
  //     if (searchTimeout) {
  //         clearTimeout(searchTimeout)
  //     }

  //     // Set a new timeout to search after 500 ms of user stopping typing
  //     const timeoutId = setTimeout(() => {
  //         fetchUserEmails(query);
  //     }, 500);

  //     setSearchTimeout(timeoutId);
  // };

  const handleSearchChange = e => {
    const query = e.target.value
    setSearchQuery(query)
    setIsSearching(true)

    // Clear any existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout)
    }

    // Set a new timeout to search after 500ms of user stopping typing
    const timeoutId = setTimeout(() => {
      fetchUserEmails(query)

      // If we have an active contact, refresh their messages with the new search
      if (activeContact && activeContact.email) {
        handleUserClick(activeContact)
      }
    }, 500)

    setSearchTimeout(timeoutId)
  }

  // Clear search
  const clearSearch = () => {
    setSearchQuery('')
    setIsSearching(false)
    fetchUserEmails('')
    if (searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }

  // Fetch messages by email
  // const handleUserClick = (user) => {
  //     setActiveContact({ name: user.name, email: user.email });
  //     setLoadingMessages(true);
  //     messageService.getDetailedMessagesByEmail(user.email, searchQuery)
  //         .then(res => {
  //             if (!res.error) {
  //                 setRecentMessages(res.data);
  //             }
  //         })
  //         .catch(err => console.error('Error fetching messages:', err))
  //         .finally(() => {
  //             setLoadingMessages(false);
  //             if (window.innerWidth < 768) setShowSidebar(false);
  //         });
  // };

  const handleUserClick = user => {
    setActiveContact({ name: user.name, email: user.email })
    setLoadingMessages(true)

    // Ensure we're passing the search query correctly
    const params = {}
    if (searchQuery && searchQuery.trim() !== '') {
      params.search = searchQuery.trim()
    }

    // Log the request for debugging
    console.log(
      'Fetching messages for email:',
      user.email,
      'with params:',
      params
    )

    messageService
      .getDetailedMessagesByEmail(user.email, params)
      .then(res => {
        console.log('Response from getDetailedMessagesByEmail:', res)
        if (!res.error) {
          setRecentMessages(res.data || [])
        } else {
          console.error('Error in response:', res.message)
          setRecentMessages([])
        }
      })
      .catch(err => {
        console.error('Error fetching messages:', err)
        setRecentMessages([])
      })
      .finally(() => {
        setLoadingMessages(false)
        if (window.innerWidth < 768) setShowSidebar(false)
      })
  }

  // Improved scroll to bottom functionality
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end'
      })
    }
  }

  // Scroll to bottom whenever messages change
  useEffect(() => {
    scrollToBottom()
  }, [recentMessages])

  // Responsive sidebar
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 768
      setShowSidebar(!isMobile)
      // Always show recent emails on desktop
      if (!isMobile) {
        setShowRecentEmails(true)
      }
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        window.innerWidth < 768 &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        !event.target.closest('.menu-toggle')
      ) {
        setShowSidebar(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSendMessage = async e => {
    e.preventDefault()
    const trimmedMessage = message.trim()
    if (!trimmedMessage || !activeContact.email) return

    const newMessage = {
      email: activeContact.email,
      content: trimmedMessage,
      sender_type: 'user',
      conversation_title: 'halo' // or derive from state if needed
    }

    try {
      const res = await messageService.sendMessage(newMessage)

      if (!res.error) {
        const sentMessage = {
          id: res.data?.id || Date.now(),
          sender_type: 'user',
          content: trimmedMessage,
          created_at: new Date().toISOString() // Fixed date conversion
        }
        setRecentMessages(prev => [...prev, sentMessage])
        setMessage('')
        // Scroll to bottom after sending a message
        setTimeout(scrollToBottom, 100)
      } else {
        console.error('Send message error:', res.message)
      }
    } catch (err) {
      console.error('Send message failed:', err)
    }
  }

  // Toggle recent emails section visibility
  const toggleRecentEmails = () => {
    setShowRecentEmails(!showRecentEmails)
  }

  // Highlight search matches in text
  const highlightSearchMatch = text => {
    if (!searchQuery || !text) return text

    const parts = text.split(new RegExp(`(${searchQuery})`, 'gi'))
    return parts.map((part, i) =>
      part.toLowerCase() === searchQuery.toLowerCase() ? (
        <span key={i} className='search-highlight'>
          {part}
        </span>
      ) : (
        part
      )
    )
  }

  // Add these functions to handle search
  // Updated handleSearch function
  const handleSearch = async () => {
    if (!searchQueryChat.trim() || !activeContact.email) return

    setIsSearchingChat(true)
    try {
      // Create search params without the undefined activeConversationId
      const searchParams = {
        limit: 100,
        offset: 0
        // Don't include conversation_id since it's not defined
      }

      const result = await messageService.searchMessagesByEmail(
        activeContact.email,
        searchQueryChat,
        searchParams
      )

      if (!result.error) {
        setRecentMessages(result.data || [])
        setSearchResults(result.data || [])
      } else {
        // Use console.error instead of toast
        console.error('Error searching messages:', result.message)
        // You can implement a custom error notification here if needed
      }
    } catch (error) {
      console.error('Search error:', error)
      // Use console.error instead of toast
    } finally {
      setIsSearchingChat(false)
    }
  }

  // Updated resetSearch function
  const resetSearch = async () => {
    if (!activeContact.email) return

    setSearchQueryChat('')
    setSearchResults([])

    // Load messages directly without a separate function call
    setLoadingMessages(true)
    try {
      const response = await messageService.getDetailedMessagesByEmail(
        activeContact.email
      )
      if (!response.error) {
        setRecentMessages(response.data || [])
      } else {
        console.error('Error loading messages:', response.message)
      }
    } catch (error) {
      console.error('Error loading messages:', error)
    } finally {
      setLoadingMessages(false)
      setShowSearch(false)
    }
  }

  // Function to highlight search results
  const isHighlighted = message => {
    if (!searchQuery) return false

    // Check if the message contains the search query
    return message.content.toLowerCase().includes(searchQuery.toLowerCase())
  }

  // Modify your loadMessages function to reset search state
  const loadMessages = async () => {
    setLoadingMessages(true)
    try {
      // Your existing code to load messages...

      // Reset search state when loading new messages
      setSearchQueryChat('')
      setSearchResults([])
      setShowSearch(false)
    } catch (error) {
      console.error('Error loading messages:', error)
      toast.error('Error loading messages. Please try again.')
    } finally {
      setLoadingMessages(false)
    }
  }

  // Format message content with boldness and lists
  const formatMessage = text => {
    if (!text) return ''

    // Process text for boldness (text between single asterisks)
    let formattedText = text.split(/(\*[^*]+\*)/).map((part, index) => {
      if (part.startsWith('*') && part.endsWith('*')) {
        // Extract the content between the asterisks
        const content = part.slice(1, -1)
        return <strong key={`bold-${index}`}>{content}</strong>
      }
      return part
    })

    // Process text for lists (text between double asterisks)
    let result = []
    let currentIndex = 0

    formattedText.forEach((part, partIndex) => {
      if (typeof part === 'string') {
        let lastPosition = 0
        let textSegments = []

        // Find all list items in the text
        const listRegex = /\*\*([^*]+)\*\*/g
        let match

        while ((match = listRegex.exec(part)) !== null) {
          // Add text before the list item
          if (match.index > lastPosition) {
            textSegments.push(
              <span key={`text-${partIndex}-${lastPosition}`}>
                {part.substring(lastPosition, match.index)}
              </span>
            )
          }

          // Add the list item
          textSegments.push(
            <li
              key={`list-${partIndex}-${match.index}`}
              className='message-list-item'
            >
              {match[1]}
            </li>
          )

          lastPosition = match.index + match[0].length
        }

        // Add any remaining text after the last list item
        if (lastPosition < part.length) {
          textSegments.push(
            <span key={`text-${partIndex}-${lastPosition}`}>
              {part.substring(lastPosition)}
            </span>
          )
        }

        // If we found list items, use the segments, otherwise use the original text
        if (textSegments.length > 0) {
          result.push(...textSegments)
        } else {
          result.push(part)
        }
      } else {
        // Preserve existing React elements (like bold text)
        result.push(part)
      }

      currentIndex++
    })

    return <div className='formatted-message'>{result}</div>
  }

  return (
    <Container fluid className='chat-container p-0'>
      <div className='d-flex chat-wrapper'>
        <GlobalMiniSidebar />

        <div className='chat-main-container'>
          {showSidebar && window.innerWidth < 768 && (
            <div
              className='sidebar-overlay'
              onClick={() => setShowSidebar(false)}
            ></div>
          )}

          <div className='d-flex h-100 position-relative'>
            {/* Sidebar */}
            <div
              ref={sidebarRef}
              className={`chat-sidebar ${
                showSidebar ? 'show-sidebar' : 'hide-sidebar'
              }`}
            >
              <div className='d-md-none sidebar-close'>
                <button
                  className='close-btn'
                  onClick={() => setShowSidebar(false)}
                >
                  <FaTimes />
                </button>
              </div>

              <div className='sidebar-header d-flex align-items-center p-3'>
                <div className='user-avatar me-3'>
                  <Image
                    src='/assets/avatars/user-1.png'
                    alt='Profile'
                    roundedCircle
                    className='avatar'
                  />
                </div>
                <div className='header-text'>
                  <h5 className='mb-0'>Chats</h5>
                </div>
              </div>

              <div className='search-box p-3'>
                <InputGroup>
                  <InputGroup.Text className='bg-light border-0'>
                    <FaSearch className='text-muted' />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder='Search messages or users'
                    className='border-0 bg-light'
                    value={searchQuery}
                    onChange={handleSearchChange}
                    ref={searchInputRef}
                  />
                  {searchQuery && (
                    <InputGroup.Text
                      className='bg-light border-0 clear-search-btn'
                      onClick={clearSearch}
                    >
                      <FaTimes className='text-muted' />
                    </InputGroup.Text>
                  )}
                </InputGroup>
              </div>

              {/* Recent Email Section - Now with toggle */}
              {/* <div className="recent-email-section px-3 pt-2">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h6 className="text-muted mb-0">
                                        {searchQuery ? 'Search Results' : 'Recent Email Active'}
                                        {isSearching && <Spinner animation="border" size="sm" className="ms-2" />}
                                    </h6>
                                    <Button 
                                        variant="link" 
                                        className="p-0 text-muted" 
                                        onClick={toggleRecentEmails}
                                    >
                                        {showRecentEmails ? <FaChevronUp /> : <FaChevronDown />}
                                    </Button>
                                </div>
                                
                                {showRecentEmails && (
                                    <div className="recent-chats">
                                        {loadingUsers && !isSearching ? (
                                            <div className="text-center my-4">
                                                <Spinner animation="border" variant="primary" />
                                            </div>
                                        ) : userEmails.length === 0 ? (
                                            <div className="text-center py-4">
                                                <p className="text-muted">
                                                    {searchQuery ? 'No results found' : 'No recent emails'}
                                                </p>
                                            </div>
                                        ) : (
                                            userEmails.map((user, idx) => (
                                                <div
                                                    key={idx}
                                                    className={`chat-item d-flex align-items-center p-2 rounded ${activeContact.email === user.email ? 'active' : ''} ${user.search_match ? 'search-match' : ''}`}
                                                    onClick={() => handleUserClick(user)}
                                                >
                                                    <div className="chat-avatar position-relative me-3">
                                                        <div className="avatar-initial">{user.name[0]}</div>
                                                    </div>
                                                    <div className="chat-details flex-grow-1">
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <h6 className="mb-0">
                                                                {searchQuery ? highlightSearchMatch(user.name) : user.name}
                                                            </h6>       
                                                            <span className="chat-time small text-muted">
                                                                {new Date(user.last_message_time).getHours().toString().padStart(2, '0')}:{new Date(user.last_message_time).getMinutes().toString().padStart(2, '0')}
                                                            </span>
                                                        </div>
                                                        
                                                        <p className="text-truncate mb-0 small">
                                                        {user.recent_messages && user.recent_messages[0] && 
                                                                (searchQuery 
                                                                    ? highlightSearchMatch(user.recent_messages[0].content.substring(0, 30) + (user.recent_messages[0].content.length > 30 ? '...' : '')) 
                                                                    : user.recent_messages[0].content.substring(0, 30) + (user.recent_messages[0].content.length > 30 ? '...' : ''))
                                                            }
                                                        </p>
                                                        
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div> */}

              {/* Recent Email Section - Now with toggle */}
              <div className='recent-email-section px-3 pt-2'>
                <div className='d-flex justify-content-between align-items-center mb-2'>
                  <h6 className='text-muted mb-0'>
                    {searchQuery ? 'Search Results' : 'Recent Email Active'}
                    {isSearching && (
                      <Spinner animation='border' size='sm' className='ms-2' />
                    )}
                  </h6>
                  <Button
                    variant='link'
                    className='p-0 text-muted'
                    onClick={toggleRecentEmails}
                  >
                    {showRecentEmails ? <FaChevronUp /> : <FaChevronDown />}
                  </Button>
                </div>

                {showRecentEmails && (
                  <div className='recent-chats'>
                    {loadingUsers && !isSearching ? (
                      <div className='text-center my-4'>
                        <Spinner animation='border' variant='primary' />
                      </div>
                    ) : userEmails.length === 0 ? (
                      <div className='text-center py-4'>
                        <p className='text-muted'>
                          {searchQuery
                            ? 'No results found'
                            : 'No recent emails'}
                        </p>
                      </div>
                    ) : (
                      userEmails.map((user, idx) => (
                        <div
                          key={idx}
                          className={`chat-item d-flex align-items-center p-2 rounded ${
                            activeContact.email === user.email ? 'active' : ''
                          } ${user.search_match ? 'search-match' : ''}`}
                          onClick={() => handleUserClick(user)}
                        >
                          <div className='chat-avatar position-relative me-3'>
                            <div className='avatar-initial'>{user.name[0]}</div>
                          </div>
                          <div className='chat-details flex-grow-1'>
                            <div className='d-flex justify-content-between align-items-center'>
                              <h6 className='mb-0'>
                                {searchQuery
                                  ? highlightSearchMatch(user.name)
                                  : user.name}
                              </h6>
                              <span className='chat-time small text-muted'>
                                {new Date(user.last_message_time)
                                  .getHours()
                                  .toString()
                                  .padStart(2, '0')}
                                :
                                {new Date(user.last_message_time)
                                  .getMinutes()
                                  .toString()
                                  .padStart(2, '0')}
                              </span>
                            </div>
                            <p className='text-truncate mb-0 small'>
                              {user.recent_messages &&
                                user.recent_messages[0] &&
                                (searchQuery
                                  ? highlightSearchMatch(
                                      user.recent_messages[0].content.substring(
                                        0,
                                        30
                                      ) +
                                        (user.recent_messages[0].content
                                          .length > 30
                                          ? '...'
                                          : '')
                                    )
                                  : user.recent_messages[0].content.substring(
                                      0,
                                      30
                                    ) +
                                    (user.recent_messages[0].content.length > 30
                                      ? '...'
                                      : ''))}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Chat area */}
            {/* Chat area with search functionality */}
            <div className='chat-content-area'>
              <div className='chat-profile-header'>
                <Row className='align-items-center m-0'>
                  <Col xs='auto' className='d-md-none menu-toggle'>
                    <button
                      className='action-icon-btn me-2'
                      onClick={() => setShowSidebar(true)}
                    >
                      <FaBars />
                    </button>
                  </Col>
                  <Col className='d-flex align-items-center'>
                    <div className='profile-avatar me-3'>
                      <Image
                        src='/assets/avatars/user-1.png'
                        alt='User'
                        roundedCircle
                        className='profile-img'
                      />
                    </div>
                    <div className='profile-info'>
                      <h4 className='mb-0 profile-name'>
                        {activeContact.name}
                      </h4>
                      <div className='online-status'>
                        <span className='status-indicator'></span>
                        <span className='status-text'>Online</span>
                      </div>
                    </div>
                  </Col>
                  <Col xs='auto' className='profile-actions'>
                    <div className='action-icons'>
                      {/* Search toggle button */}
                      <Button
                        variant='link'
                        onClick={() => setShowSearch(!showSearch)}
                        className={showSearch ? 'active' : ''}
                      >
                        <FaSearch />
                      </Button>
                      <Button variant='link'>
                        <FaPhone />
                      </Button>
                      <Button variant='link'>
                        <FaVideo />
                      </Button>
                      <Button variant='link'>
                        <FaUsers />
                      </Button>
                      <Button variant='link'>
                        <FaEllipsisH />
                      </Button>
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Search bar - conditionally rendered */}
              {showSearch && (
                <div className='chat-search-bar p-2'>
                  <InputGroup>
                    <InputGroup.Text>
                      <FaSearch />
                    </InputGroup.Text>
                    <Form.Control
                      type='text'
                      placeholder='Search messages...'
                      value={searchQueryChat}
                      onChange={e => setSearchQueryChat(e.target.value)}
                      onKeyPress={e => e.key === 'Enter' && handleSearch()}
                    />
                    {searchQueryChat && (
                      <Button
                        variant='outline-secondary'
                        onClick={() => {
                          setSearchQueryChat('')
                          resetSearch()
                        }}
                      >
                        <FaTimes />
                      </Button>
                    )}
                    <Button variant='primary' onClick={handleSearch}>
                      Search
                    </Button>
                  </InputGroup>
                </div>
              )}

              {/* Message List */}
              <div
                className='chat-messages-container'
                ref={messageContainerRef}
              >
                <div className='chat-messages p-3'>
                  <div className='message-date text-center mb-3'>
                    <span className='badge bg-light text-secondary'>
                      {new Date().toLocaleDateString('en-US', {
                        weekday: 'long',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </span>
                  </div>

                  {loadingMessages ? (
                    <div className='text-center'>
                      <Spinner animation='border' variant='primary' />
                    </div>
                  ) : recentMessages.length > 0 ? (
                    recentMessages.map(msg => (
                      <div
                        key={msg.id}
                        className={`message-wrapper ${
                          msg.sender_type === 'user' ? 'outgoing' : 'incoming'
                        } ${isHighlighted(msg) ? 'highlighted' : ''}`}
                      >
                        {msg.sender_type !== 'user' && (
                          <div className='message-avatar'>
                            <Image
                              src='/assets/avatars/user-1.png'
                              alt='AI'
                              roundedCircle
                              className='avatar-sm'
                            />
                          </div>
                        )}
                        <div className='message-content'>
                          <div
                            className={`message-bubble ${
                              msg.sender_type === 'user'
                                ? 'outgoing-bubble'
                                : 'incoming-bubble'
                            }`}
                          >
                            {formatMessage(msg.content)}
                          </div>
                          <div className='message-info d-flex align-items-center'>
                            <span className='message-time text-muted small'>
                              {new Date(msg.created_at).toLocaleTimeString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className='text-center text-muted'>
                      <p>No messages found</p>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </div>

              {/* Message Input */}
              <div className='chat-input-area'>
                <Form onSubmit={handleSendMessage}>
                  <InputGroup>
                    <Button
                      variant='light'
                      className='border d-none d-sm-block'
                    >
                      <FaSmile />
                    </Button>
                    <Button variant='light' className='border'>
                      <FaPaperclip />
                    </Button>
                    <Form.Control
                      type='text'
                      value={message}
                      onChange={e => setMessage(e.target.value)}
                      placeholder='Enter Message...'
                      className='border'
                    />
                    <Button variant='primary' type='submit'>
                      <FaPaperPlane />
                    </Button>
                  </InputGroup>
                </Form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Container>
  )
}

export default ChatPage
